import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import { fetchKanbanStages, fetchKanbanTickets, getCanonicalStageId, mergeTicketPages, moveKanbanTicket, normalizeStageTarget } from "../../services/kanban";
import { confirmMove, emptyBoard, emptyFilters, moveInBoard } from "./boardState";
import { applyContactPhotoPatch } from "../../services/contactPhoto";
import useContactPhotoUpdates from "../../hooks/useContactPhotoUpdates";

export default function useKanbanBoard({ user, socket }) {
  const [board, setBoard] = useState(emptyBoard);
  const [stages, setStages] = useState([]);
  const [filters, setFilters] = useState(emptyFilters);
  const [pendingMoves, setPendingMoves] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [stagesError, setStagesError] = useState(false);
  const state = useRef({ mounted: false, board: emptyBoard(), stages: [], filters: emptyFilters, generation: 0, revision: 0, read: null, queued: null, refresh: false, timer: null, stagesRequest: 0, moves: new Map(), uncertain: new Set() });
  const drainRef = useRef(() => {});

  const updateBoard = useCallback(next => {
    state.current.board = typeof next === "function" ? next(state.current.board) : next;
    setBoard(state.current.board);
  }, []);
  const mergePhotoSnapshot = useContactPhotoUpdates({ companyId: user.companyId, socket,
    onPatch: patch => updateBoard(current => applyContactPhotoPatch(current, patch)) });

  const loadStages = useCallback(async () => {
    const current = state.current;
    const request = ++current.stagesRequest;
    try {
      const { data } = await fetchKanbanStages();
      if (!current.mounted || request !== current.stagesRequest) return;
      current.stages = data.lista || [];
      setStages(current.stages);
      setStagesError(false);
    } catch (_) {
      if (current.mounted && request === current.stagesRequest) setStagesError(true);
    }
  }, []);

  const drain = useCallback(async () => {
    const current = state.current;
    if (!current.mounted || current.read || current.moves.size || (!current.queued && !current.refresh)) return;
    const kind = current.queued || "refresh";
    current.queued = null;
    if (kind !== "append") current.refresh = false;
    if (kind === "append" && (!current.board.hasMore || !current.board.nextCursor)) {
      setLoadingMore(false);
      drainRef.current();
      return;
    }
    const read = { kind, generation: current.generation, revision: current.revision };
    current.read = read;
    const activeFilters = { ...current.filters, users: current.filters.onlyMine ? [user.id] : [] };
    const append = kind === "append";
    const desiredCount = kind === "refresh" ? Math.max(100, current.board.tickets.length) : 100;
    if (append) setLoadingMore(true);
    else if (kind === "replace") setLoading(true);
    else setRefreshing(true);
    const valid = () => current.mounted && current.read === read && current.generation === read.generation && current.revision === read.revision;
    try {
      let { data } = await fetchKanbanTickets({ filters: activeFilters, afterId: append ? current.board.nextCursor : null });
      if (!valid()) return;
      data = { ...data, tickets: data.tickets || [] };
      const cursors = new Set();
      while (kind === "refresh" && data.hasMore && data.nextCursor && data.tickets.length < desiredCount) {
        if (cursors.has(data.nextCursor)) throw new Error("Cursor repetido");
        cursors.add(data.nextCursor);
        const next = await fetchKanbanTickets({ filters: activeFilters, afterId: data.nextCursor });
        if (!valid()) return;
        data = { ...data, tickets: mergeTicketPages(data.tickets, next.data.tickets || []), hasMore: next.data.hasMore, nextCursor: next.data.nextCursor };
      }
      const tickets = mergePhotoSnapshot(data.tickets, current.board.tickets);
      updateBoard({ tickets: append ? mergeTicketPages(current.board.tickets, tickets) : tickets,
        count: Number(data.count) || 0, laneCounts: data.laneCounts || { none: 0 }, hasMore: Boolean(data.hasMore), nextCursor: data.nextCursor || null });
      if (append) data.tickets.forEach(ticket => current.uncertain.delete(Number(ticket.id)));
      else current.uncertain.clear();
      setError("");
    } catch (_) {
      if (valid()) setError(kind === "append" ? "Não foi possível carregar mais atendimentos." : "Não foi possível atualizar o quadro.");
    } finally {
      if (current.mounted && current.read === read) {
        // A movement invalidates the response, not the user's request for another page.
        if (append && current.generation === read.generation && current.revision !== read.revision && current.queued !== "replace") {
          current.queued = "append";
        }
        current.read = null;
        setLoading(false); setLoadingMore(false); setRefreshing(false);
        drainRef.current();
      }
    }
  }, [updateBoard, user.id, mergePhotoSnapshot]);
  drainRef.current = drain;

  const scheduleRefresh = useCallback(() => {
    const current = state.current;
    // A fixed window coalesces bursts without starving updates during continuous traffic.
    if (current.timer || !current.mounted) return;
    current.timer = setTimeout(() => {
      current.timer = null;
      current.refresh = true;
      drainRef.current();
    }, 450);
  }, []);

  const applyFilters = useCallback(next => {
    const current = state.current;
    current.filters = { ...emptyFilters, ...next };
    current.generation += 1;
    current.queued = "replace";
    current.refresh = false;
    setFilters(current.filters);
    setLoading(true);
    drainRef.current();
  }, []);

  const refresh = useCallback(() => {
    loadStages();
    state.current.refresh = true;
    drainRef.current();
  }, [loadStages]);

  const loadMore = useCallback(() => {
    const current = state.current;
    if (current.queued === "replace" || current.queued === "append" || current.read?.kind === "append" || loadingMore) return;
    current.queued = "append";
    setLoadingMore(true);
    drainRef.current();
  }, [loadingMore]);

  const move = useCallback(async (ticketId, laneId) => {
    const current = state.current;
    const id = Number(ticketId);
    const ticket = current.board.tickets.find(item => Number(item.id) === id);
    if (!ticket || current.moves.has(id)) return;
    if (current.uncertain.has(id)) { toast.warn("Atualize o quadro para confirmar a etapa deste atendimento."); refresh(); return; }
    let stageId;
    try { stageId = normalizeStageTarget(laneId); } catch (err) { toast.error(err.message); return; }
    if (getCanonicalStageId(ticket) === stageId) return;
    const stage = stageId ? current.stages.find(item => Number(item.id) === stageId) : null;
    if (stageId && !stage) { toast.error("Esta etapa não está mais disponível."); refresh(); return; }
    const command = { ticketId: id, stage, enteredAt: new Date().toISOString() };
    const payload = { ticketId: id, stageTagId: stageId, expectedVersion: Number(ticket.kanbanVersion) || 0, requestId: uuidv4() };
    const generation = current.generation;
    current.revision += 1; // An earlier GET must never overwrite a movement in flight.
    current.moves.set(id, command);
    setPendingMoves(Object.fromEntries(current.moves));
    try {
      let response;
      try { response = await moveKanbanTicket(payload); }
      catch (err) {
        const status = err.response && err.response.status;
        if (status && status < 500) throw err;
        // Replay the exact command once: the server deduplicates by requestId.
        response = await moveKanbanTicket(payload);
      }
      if (!current.mounted) return;
      updateBoard(boardValue => confirmMove(boardValue, { ...command, version: response.data.version }, current.filters));
      toast.success(`Movido para ${stage ? stage.name : "Sem etapa"}.`);
    } catch (err) {
      if (!current.mounted) return;
      const status = err.response && err.response.status;
      if (!status || status >= 500) {
        current.uncertain.add(id);
        toast.warn("Não foi possível confirmar o movimento. Atualize o quadro.");
      } else toastError(err);
    } finally {
      current.moves.delete(id);
      current.revision += 1;
      if (current.mounted) {
        setPendingMoves(Object.fromEntries(current.moves));
        current.refresh = true;
        if (generation !== current.generation) current.queued = "replace";
        drainRef.current();
      }
    }
  }, [refresh, updateBoard]);

  useEffect(() => {
    const current = state.current;
    current.mounted = true;
    current.queued = "replace";
    loadStages(); drainRef.current();
    const event = data => { if (!data?.action || ["invalidate", "create", "update", "delete"].includes(data.action)) scheduleRefresh(); };
    const tagEvent = data => {
      if (data?.tag && Number(data.tag.kanban) !== 1) return;
      loadStages(); scheduleRefresh();
    };
    const names = [`company-${user.companyId}-kanban`, `company-${user.companyId}-ticket`, `company-${user.companyId}-appMessage`];
    if (socket) { names.forEach(name => socket.on(name, event)); socket.on(`company${user.companyId}-tag`, tagEvent); }
    return () => {
      current.mounted = false;
      current.generation += 1; current.stagesRequest += 1;
      clearTimeout(current.timer); current.timer = null;
      if (socket) { names.forEach(name => socket.off(name, event)); socket.off(`company${user.companyId}-tag`, tagEvent); }
    };
  }, [loadStages, scheduleRefresh, socket, user.companyId]);

  const visibleBoard = useMemo(() => Object.values(pendingMoves).reduce(moveInBoard, board), [board, pendingMoves]);
  return { ...visibleBoard, stages, filters, pendingMoves, loading, refreshing, loadingMore, error, stagesError, applyFilters, refresh, loadMore, move };
}
